import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "../axios";
import Select from "react-select";
import "./GstFilingStatus.css";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

const GstFilingStatus = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [yearMonths, setYearMonths] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // ✅ Enhanced Filter States
  const [filters, setFilters] = useState({
    yearMonth: null,
    remarks: null,
    trader: null,
    gstrType: null,
    unfiledOnly: false,
    amountNotReceived: false,
    dateRange: null,
    customStartDate: "",
    customEndDate: "",
    minFees: "",
    maxFees: "",
    searchQuery: "",
  });

  // ✅ Statistics State
  const [stats, setStats] = useState({
    total: 0,
    filed: 0,
    notFiled: 0,
    amountReceived: 0,
    amountPending: 0,
    totalFees: 0,
  });

  const [formData, setFormData] = useState({
    trade_name: null,
    year_month: null,
    gstr: null,
    received_on: "",
    fees_amount: "",
    remarks: null,
    amount_received: false,
  });

  // ✅ Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#2563eb',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#dbeafe' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      cursor: 'pointer',
      padding: '10px 12px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  // ✅ Prepare options for react-select
  // ✅ Prepare options for react-select with chronological sorting
const yearMonthOptions = useMemo(() => {
  const monthOrder = {
    'January': 1, 'Jan': 1,
    'February': 2, 'Feb': 2,
    'March': 3, 'Mar': 3,
    'April': 4, 'Apr': 4,
    'May': 5,
    'June': 6, 'Jun': 6,
    'July': 7, 'Jul': 7,
    'August': 8, 'Aug': 8,
    'September': 9, 'Sep': 9, 'Sept': 9,
    'October': 10, 'Oct': 10,
    'November': 11, 'Nov': 11,
    'December': 12, 'Dec': 12
  };
  
  return yearMonths
    .map(ym => ({
      value: ym.id,
      label: `${ym.month} ${ym.year}`,
      year: parseInt(ym.year),
      monthNum: monthOrder[ym.month] || 0
    }))
    .sort((a, b) => {
      // Sort by year first (ascending)
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      // Then by month number (ascending)
      return a.monthNum - b.monthNum;
    });
}, [yearMonths]);

  const traderOptions = useMemo(() => 
    credentials.map(cred => ({
      value: cred.id,
      label: cred.trade_name
    })),
    [credentials]
  );

  const gstrTypeOptions = [
    { value: "gstr1", label: "GSTR-1" },
    { value: "gstr3b", label: "GSTR-3B" },
    { value: "gstr9", label: "GSTR-9" },
    { value: "gstr9c", label: "GSTR-9C" },
    { value: "cmp08", label: "CMP-08" },
  ];

  const remarksOptions = [
    { value: "Filed", label: "✅ Filed" },
    { value: "Not Filed", label: "⏳ Not Filed" },
  ];

  const dateRangeOptions = [
    { value: "all", label: "All Time" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "custom", label: "Custom Range" },
  ];

  // ✅ Calculate Statistics - wrapped in useCallback
  const calculateStats = useCallback((data) => {
    const total = data.length;
    const filed = data.filter(r => r.remarks === "Filed").length;
    const notFiled = total - filed;
    const amountReceived = data.filter(r => r.amount_received).length;
    const amountPending = total - amountReceived;
    const totalFees = data.reduce((sum, r) => sum + (parseFloat(r.fees_amount) || 0), 0);

    setStats({ total, filed, notFiled, amountReceived, amountPending, totalFees });
  }, []);

  // ✅ Fetch Data - wrapped in useCallback
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.get(`${API_URL}api/gst-filing-status/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data);
      calculateStats(res.data);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  }, [calculateStats]);

  // ✅ Fetch Dropdowns - wrapped in useCallback
  const fetchDropdowns = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    try {
      const [credRes, ymRes] = await Promise.all([
        axios.get(`${API_URL}api/gst/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}api/yearly-months/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCredentials(credRes.data);
      setYearMonths(ymRes.data);
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
    }
  }, []);

  // ✅ useEffect with proper dependencies
  useEffect(() => {
    fetchData();
    fetchDropdowns();
  }, [fetchData, fetchDropdowns]);

  // ✅ Advanced Filtering Logic - wrapped in useCallback
  const getFilteredRecords = useCallback(() => {
    let filtered = [...records];

    if (filters.yearMonth) {
      filtered = filtered.filter(r => String(r.year_month) === String(filters.yearMonth.value));
    }

    if (filters.remarks) {
      filtered = filtered.filter(r => (r.remarks || "Not Filed") === filters.remarks.value);
    }

    if (filters.trader) {
      filtered = filtered.filter(r => String(r.trade_name) === String(filters.trader.value));
    }

    if (filters.gstrType) {
      filtered = filtered.filter(r => r.gstr === filters.gstrType.value);
    }

    if (filters.unfiledOnly) {
      filtered = filtered.filter(r => (r.remarks || "Not Filed") === "Not Filed");
    }

    if (filters.amountNotReceived) {
      filtered = filtered.filter(r => !r.amount_received);
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const tradeName = (r.trade_name_display || "").toLowerCase();
        return tradeName.includes(query);
      });
    }

    if (filters.dateRange && filters.dateRange.value !== "all" && filters.dateRange.value !== "custom") {
      const today = new Date();
      let startDate;

      switch (filters.dateRange.value) {
        case "this_month":
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case "last_month":
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(r => {
          const receivedDate = r.received_on ? new Date(r.received_on) : null;
          return receivedDate && receivedDate >= startDate;
        });
      }
    }

    if (filters.dateRange && filters.dateRange.value === "custom" && filters.customStartDate && filters.customEndDate) {
      const start = new Date(filters.customStartDate);
      const end = new Date(filters.customEndDate);
      filtered = filtered.filter(r => {
        const receivedDate = r.received_on ? new Date(r.received_on) : null;
        return receivedDate && receivedDate >= start && receivedDate <= end;
      });
    }

    if (filters.minFees) {
      filtered = filtered.filter(r => (parseFloat(r.fees_amount) || 0) >= parseFloat(filters.minFees));
    }
    if (filters.maxFees) {
      filtered = filtered.filter(r => (parseFloat(r.fees_amount) || 0) <= parseFloat(filters.maxFees));
    }

    return filtered;
  }, [records, filters]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      yearMonth: null,
      remarks: null,
      trader: null,
      gstrType: null,
      unfiledOnly: false,
      amountNotReceived: false,
      dateRange: null,
      customStartDate: "",
      customEndDate: "",
      minFees: "",
      maxFees: "",
      searchQuery: "",
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({ 
      ...prev, 
      [name]: selectedOption 
    }));
  };

  // ✅ Auto Create - with proper dependencies
  const handleAutoCreateAll = useCallback(async () => {
    if (!formData.year_month || !formData.gstr) {
      alert("⚠️ Please select both GSTR Type and Period");
      return;
    }

    const token = localStorage.getItem("access_token");
    const existingCombinations = new Set(
      records.map((r) => `${r.trade_name}-${r.year_month}-${r.gstr}`)
    );

    const payloads = credentials
      .filter((cred) => {
        const key = `${cred.id}-${formData.year_month.value}-${formData.gstr.value}`;
        return !existingCombinations.has(key);
      })
      .map((cred) => ({
        trade_name: cred.id,
        year_month: formData.year_month.value,
        gstr: formData.gstr.value,
        received_on: null,
        fees_amount: null,
        remarks: "",
        amount_received: false,
      }));

    if (payloads.length === 0) {
      alert("✅ All traders already have records for this period");
      return;
    }

    try {
      for (const payload of payloads) {
        await axios.post(`${API_URL}api/gst-filing-status/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      alert(`✅ Auto-created ${payloads.length} new filings for ${formData.gstr.label}`);
      fetchData();
    } catch (err) {
      console.error("❌ Auto-create error:", err.response?.data || err.message);
      alert("Error creating records. Please try again.");
    }
  }, [credentials, formData.gstr, formData.year_month, records, fetchData]);

  const cleanPayload = (data) => ({
    trade_name: data.trade_name?.value || data.trade_name,
    year_month: data.year_month?.value || data.year_month,
    gstr: data.gstr?.value || data.gstr,
    received_on: data.received_on === "" ? null : data.received_on,
    fees_amount: data.fees_amount === "" ? null : data.fees_amount,
    remarks: data.remarks?.value || data.remarks || "",
    amount_received: Boolean(data.amount_received),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("access_token");

    const exists = records.some(
      (rec) =>
        String(rec.trade_name) === String(formData.trade_name?.value || formData.trade_name) &&
        String(rec.year_month) === String(formData.year_month?.value || formData.year_month) &&
        rec.gstr === (formData.gstr?.value || formData.gstr)
    );

    if (!editingId && exists) {
      alert("❌ A record with the selected Trade Name, Year & Month, and GSTR already exists.");
      return;
    }

    try {
      const payload = cleanPayload(formData);

      if (editingId) {
        await axios.put(`${API_URL}api/gst-filing-status/${editingId}/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}api/gst-filing-status/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      fetchData();
      setFormVisible(false);
      setEditingId(null);
      setFormData({
        trade_name: null,
        year_month: null,
        gstr: null,
        received_on: "",
        fees_amount: "",
        remarks: null,
        amount_received: false,
      });
    } catch (err) {
      console.error("Submission Error:", err.response?.data || err.message);
      alert("❌ Error: " + (err.response?.data?.detail || "Failed to submit"));
    }
  };

  const handleEdit = (record) => {
    setFormData({
      trade_name: traderOptions.find(opt => opt.value === record.trade_name),
      year_month: yearMonthOptions.find(opt => opt.value === record.year_month),
      gstr: gstrTypeOptions.find(opt => opt.value === record.gstr),
      received_on: record.received_on || "",
      fees_amount: record.fees_amount || "",
      remarks: remarksOptions.find(opt => opt.value === record.remarks) || remarksOptions[1],
      amount_received: Boolean(record.amount_received),
    });
    setEditingId(record.id);
    setFormVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    const token = localStorage.getItem("access_token");
    try {
      await axios.delete(`${API_URL}api/gst-filing-status/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete record");
    }
  };

  const filteredRecords = getFilteredRecords();

  return (
    <div className="gst-filing-status">
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h2 className="page-title">📄 GST Filing Status</h2>
          <button onClick={() => navigate('/gst-summary')} className="summary-btn">
            <span className="btn-icon">📊</span>
            <span className="btn-text">View Analytics</span>
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="stats-grid">
        <div className="stat-card stat-total">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h4>Total Records</h4>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>

        <div className="stat-card stat-success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h4>Filed</h4>
            <p className="stat-value">{stats.filed}</p>
          </div>
        </div>

        <div className="stat-card stat-danger">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h4>Not Filed</h4>
            <p className="stat-value">{stats.notFiled}</p>
          </div>
        </div>

        <div className="stat-card stat-payment">
          <div className="stat-icon">💳</div>
          <div className="stat-content">
            <h4>Amount Received</h4>
            <p className="stat-value">{stats.amountReceived}</p>
          </div>
        </div>

        <div className="stat-card stat-warning">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h4>Payment Pending</h4>
            <p className="stat-value">{stats.amountPending}</p>
          </div>
        </div>

        <div className="stat-card stat-money">
          <div className="stat-icon">💵</div>
          <div className="stat-content">
            <h4>Total Fees</h4>
            <p className="stat-value">₹{stats.totalFees.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Advanced Filters Section with Searchable Selects */}
      <div className="filters-container">
        <h3>🔍 Advanced Filters</h3>
        
        <div className="filters-grid">
          {/* Period Filter */}
          <div className="filter-group">
            <label className="filter-label">Period</label>
            <Select
              value={filters.yearMonth}
              onChange={(option) => handleFilterChange("yearMonth", option)}
              options={yearMonthOptions}
              isClearable
              placeholder="All Periods"
              styles={customSelectStyles}
              menuPortalTarget={document.body}
            />
          </div>

          {/* Status Filter */}
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <Select
              value={filters.remarks}
              onChange={(option) => handleFilterChange("remarks", option)}
              options={remarksOptions}
              isClearable
              placeholder="All Statuses"
              styles={customSelectStyles}
              menuPortalTarget={document.body}
            />
          </div>

          {/* Trader Filter */}
          <div className="filter-group">
            <label className="filter-label">Trader</label>
            <Select
              value={filters.trader}
              onChange={(option) => handleFilterChange("trader", option)}
              options={traderOptions}
              isClearable
              placeholder="All Traders"
              styles={customSelectStyles}
              menuPortalTarget={document.body}
            />
          </div>

          {/* GSTR Type Filter */}
          <div className="filter-group">
            <label className="filter-label">GSTR Type</label>
            <Select
              value={filters.gstrType}
              onChange={(option) => handleFilterChange("gstrType", option)}
              options={gstrTypeOptions}
              isClearable
              placeholder="All Types"
              styles={customSelectStyles}
              menuPortalTarget={document.body}
            />
          </div>

          {/* Date Range Filter */}
          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <Select
              value={filters.dateRange}
              onChange={(option) => handleFilterChange("dateRange", option)}
              options={dateRangeOptions}
              isClearable
              placeholder="All Time"
              styles={customSelectStyles}
              menuPortalTarget={document.body}
            />
          </div>

          {/* Search */}
          <div className="filter-group">
            <label className="filter-label">Search Trade Name</label>
            <input
              type="text"
              placeholder="Search..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange("searchQuery", e.target.value)}
              className="filter-select"
            />
          </div>

          {/* Min Fees */}
          <div className="filter-group">
            <label className="filter-label">Min Fees</label>
            <input
              type="number"
              placeholder="Min amount"
              value={filters.minFees}
              onChange={(e) => handleFilterChange("minFees", e.target.value)}
              className="filter-select"
            />
          </div>

          {/* Max Fees */}
          <div className="filter-group">
            <label className="filter-label">Max Fees</label>
            <input
              type="number"
              placeholder="Max amount"
              value={filters.maxFees}
              onChange={(e) => handleFilterChange("maxFees", e.target.value)}
              className="filter-select"
            />
          </div>
        </div>

        {/* Custom Date Range Inputs */}
        {filters.dateRange && filters.dateRange.value === "custom" && (
          <div className="custom-date-range">
            <div className="filter-group">
              <label className="filter-label">Start Date</label>
              <input
                type="date"
                value={filters.customStartDate}
                onChange={(e) => handleFilterChange("customStartDate", e.target.value)}
                className="filter-select"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Date</label>
              <input
                type="date"
                value={filters.customEndDate}
                onChange={(e) => handleFilterChange("customEndDate", e.target.value)}
                className="filter-select"
              />
            </div>
          </div>
        )}

        {/* Checkbox Filters */}
        <div className="checkbox-filters">
          <div className="filter-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.unfiledOnly}
                onChange={(e) => handleFilterChange("unfiledOnly", e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">⏳ Show Only Not Filed</span>
            </label>
          </div>

          <div className="filter-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.amountNotReceived}
                onChange={(e) => handleFilterChange("amountNotReceived", e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">💰 Show Amount Not Received</span>
            </label>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={clearFilters} className="clear-filters-btn">
            🔄 Clear All Filters
          </button>
          <span className="filter-results">
            Showing {filteredRecords.length} of {records.length} records
          </span>
        </div>
      </div>

      {/* Action Controls with Searchable Selects */}
      <div className="action-controls">
        <div className="control-group">
          <Select
            value={formData.gstr}
            onChange={(option) => handleSelectChange("gstr", option)}
            options={gstrTypeOptions}
            placeholder="Select GSTR Type"
            styles={customSelectStyles}
            menuPortalTarget={document.body}
          />

          <Select
            value={formData.year_month}
            onChange={(option) => handleSelectChange("year_month", option)}
            options={yearMonthOptions}
            placeholder="Select Period"
            styles={customSelectStyles}
            menuPortalTarget={document.body}
          />
        </div>

        <div className="action-buttons">
          <button
            onClick={handleAutoCreateAll}
            className="action-btn auto-create-btn"
            disabled={!formData.gstr || !formData.year_month}
          >
            <span className="btn-icon">⚙️</span>
            <span className="btn-text">Auto-Create</span>
          </button>

          <button
            onClick={() => setFormVisible(!formVisible)}
            className="action-btn add-btn"
          >
            <span className="btn-icon">{formVisible ? "✖" : "➕"}</span>
            <span className="btn-text">
              {formVisible ? "Cancel" : "Add Manual Filing"}
            </span>
          </button>
        </div>
      </div>

      {/* Form Section with Searchable Selects */}
      {formVisible && (
        <div className="form-container">
          <form onSubmit={handleSubmit} className="gst-form">
            <div className="form-header">
              <h3>{editingId ? "Edit Filing Record" : "New Filing Record"}</h3>
            </div>

            <div className="form-grid">
              <div className="form-field">
                <label className="field-label">Trade Name *</label>
                <Select
                  value={formData.trade_name}
                  onChange={(option) => handleSelectChange("trade_name", option)}
                  options={traderOptions}
                  placeholder="Select Trade Name"
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  required
                />
              </div>

              <div className="form-field">
                <label className="field-label">Period *</label>
                <Select
                  value={formData.year_month}
                  onChange={(option) => handleSelectChange("year_month", option)}
                  options={yearMonthOptions}
                  placeholder="Select Period"
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  required
                />
              </div>

              <div className="form-field">
                <label className="field-label">GSTR Type *</label>
                <Select
                  value={formData.gstr}
                  onChange={(option) => handleSelectChange("gstr", option)}
                  options={gstrTypeOptions}
                  placeholder="Select GSTR Type"
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  required
                />
              </div>

              <div className="form-field">
                <label className="field-label">Received On</label>
                <input
                  type="date"
                  name="received_on"
                  value={formData.received_on}
                  onChange={handleChange}
                  className="field-input"
                />
              </div>

              <div className="form-field">
                <label className="field-label">Fees Amount</label>
                <input
                  type="number"
                  name="fees_amount"
                  placeholder="Enter amount"
                  value={formData.fees_amount}
                  onChange={handleChange}
                  className="field-input"
                />
              </div>

              <div className="form-field">
                <label className="field-label">Remarks *</label>
                <Select
                  value={formData.remarks}
                  onChange={(option) => handleSelectChange("remarks", option)}
                  options={remarksOptions}
                  placeholder="Select Status"
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  required
                />
              </div>

              <div className="form-field checkbox-field">
                <label className="field-checkbox">
                  <input
                    type="checkbox"
                    name="amount_received"
                    checked={formData.amount_received}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">Amount Received</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingId ? "Update Record" : "Create Record"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table Section */}
      <div className="table-container">
        <table className="records-table">
          <thead>
            <tr>
              <th>Trade Name</th>
              <th>GSTR</th>
              <th>Year</th>
              <th>Month</th>
              <th>Received On</th>
              <th>Fees</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length > 0 ? (
              filteredRecords.map((rec) => {
                const isNotFiled = !rec.received_on && !rec.fees_amount;
                const isAmountNotReceived = !rec.amount_received;
                const rowClass = isNotFiled
                  ? "row-not-filed"
                  : isAmountNotReceived
                  ? "row-pending-payment"
                  : "row-completed";

                const remarksDisplay = rec.remarks || "Not Filed";

                return (
                  <tr key={rec.id} className={rowClass}>
                    <td className="td-trade-name">{rec.trade_name_display}</td>
                    <td className="td-gstr">
                      <span className="gstr-badge">{rec.gstr?.toUpperCase()}</span>
                    </td>
                    <td>{rec.year}</td>
                    <td>{rec.month}</td>
                    <td>{rec.received_on || "-"}</td>
                    <td className="td-amount">
                      {rec.fees_amount ? `₹${Number(rec.fees_amount).toLocaleString()}` : "-"}
                    </td>
                    <td>
                      <span className={`status-badge status-${remarksDisplay.toLowerCase().replace(' ', '-')}`}>
                        {remarksDisplay}
                      </span>
                    </td>
                    <td>
                      <span className={`payment-badge ${rec.amount_received ? 'payment-received' : 'payment-pending'}`}>
                        {rec.amount_received ? "✓ Received" : "⏳ Pending"}
                      </span>
                    </td>
                    <td className="td-actions">
                      <button
                        onClick={() => handleEdit(rec)}
                        className="table-btn edit-btn"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(rec.id)}
                        className="table-btn delete-btn"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="no-records">
                  <div className="empty-state">
                    <span className="empty-icon">📭</span>
                    <p>No records found matching your filters</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GstFilingStatus;